// PERSONALISE — the grouped rows of the home's one customize panel
// (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1; Nika's pick 2026-09-28 row 4 "A, one
// panel, grouped"). They mount INSIDE the existing Customize drawer
// (pages/main.js `CustomizeDrawer`) above its widget list — one panel, never a
// second one:
//
//   App strip   on / off · the style (Dock | Rail) · names on / off · the apps
//               in their order (drag, or the arrow buttons)
//   Theme       Auto | Light | Dark — the existing `themeMode` preference, the
//               same explicit-theme path the account menu takes
//   Background  every sky of the ambient files index (Clouds · Dunes · Hills
//               · Lake · Window …) and None — the person's own sky over the
//               workspace's (None = no sky on the ambient ground), and a way
//               back to the workspace's
//   Sky layout  its own group, captioned like the others: Sky and mountains
//               (the sky at the top, the mountain ground at the end of the
//               scroll — the default) |
//               Clouds below (the bottom band, as before). The shorter pair:
//               "Clouds and mountains | Clouds at the bottom" overflowed the
//               355 px drawer (344 px of segments in 323 px)
//   Summary     Brief | Signals
//   Getting started   the new-account welcome on demand: the setup wizard,
//               the assistant's setup conversation and the quick tour
//               (functions/onboarding.js onboardingReplay — a replay writes
//               no decision)
//   (Widgets)   the drawer's own show / hide list stays under these groups
//
// Every change is a decision written at once (functions/homeLook.js) and the
// board behind the panel re-renders with it — the live preview is the board.
// The controls are the kit's: SwitchInput, AppSegmented, CompactIconButton.

// A group: its caption, then its rows.
export const HomePersonalizeGroup = {
  tag: 'section',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  minWidth: '0',
  CaptionTitle: {
    fontSize: 'Y2',
    margin: '0',
    align: 'center flex-start',
    Text: { text: '' }
  }
}

// A switch row: the whole row is the switch's <label>, so the words name the
// switch and a click on them flips it. `onCheck` (the kit's switch contract)
// reads the value; `onClick` writes it.
export const HomePersonalizeSwitch = {
  tag: 'label',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'Z',
  minWidth: '0',
  cursor: 'pointer',
  RowLabel: {
    tag: 'span',
    flex: '1 1 auto',
    minWidth: '0',
    fontSize: 'Z2',
    lineHeight: '1.4',
    color: 'title',
    overflowWrap: 'normal',
    text: ''
  },
  SwitchInput: {}
}

// A choice row: its words above the segmented control (the words and the
// segments wrap on a narrow panel, never clip).
export const HomePersonalizeChoice = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'Y',
  minWidth: '0',
  RowLabel: {
    tag: 'span',
    fontSize: 'Z2',
    lineHeight: '1.4',
    color: 'title',
    overflowWrap: 'normal',
    text: ''
  },
  AppSegmented: {}
}

// One app of the App strip, in the panel's list: drag it onto another app, or
// move it one place with the arrow buttons (the keyboard path). The order is
// the home layout's (functions/homeLook.js `homeLookMoveApp` → the board's
// own ops), so the strip — Dock or Rail — follows at once.
export const HomePersonalizeApp = {
  class: 'home-personalize-app',
  draggable: 'true',
  display: 'flex',
  alignItems: 'center',
  gap: 'Z',
  minWidth: '0',
  padding: 'X Y',
  round: 'radiusControl',
  cursor: 'grab',
  transition: 'background-color A defaultBezier',
  ':hover': { background: 'hover' },
  '&[data-drop-target]': { background: 'selected' },
  attr: { 'data-drop-target': (el, s) => (s && s.dropTarget ? '' : null) },
  onDragstart: (e, el, s) => {
    if (!e.dataTransfer || !s || !s.id) return
    e.dataTransfer.setData('text/plain', s.id)
    e.dataTransfer.effectAllowed = 'move'
  },
  onDragover: (e, el, s) => {
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
    if (s && !s.dropTarget && typeof s.update === 'function') s.update({ dropTarget: true })
  },
  onDragleave: (e, el, s) => {
    if (s && s.dropTarget && typeof s.update === 'function') s.update({ dropTarget: false })
  },
  onDrop: (e, el, s) => {
    e.preventDefault()
    if (s && typeof s.update === 'function') s.update({ dropTarget: false })
    const from = e.dataTransfer ? e.dataTransfer.getData('text/plain') : ''
    if (from && s && s.id) el.call('homeLookMoveApp', from, s.id)
  },

  AppGlyph: {
    theme: 'layer',
    color: 'title',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
    boxSize: 'avatarXs',
    round: 'radiusControl',
    Icon: { name: (el, s) => (s && s.icon) || 'star', boxSize: 'iconSm' }
  },
  AppName: {
    tag: 'span',
    flex: '1 1 auto',
    minWidth: '0',
    fontSize: 'Z2',
    lineHeight: '1.4',
    color: 'title',
    overflowWrap: 'normal',
    text: (el, s) => el.call('polyglot', (s && (s.title || s.id)) || '')
  },
  CompactIconButton: {
    icon: 'chevronUp',
    disabled: (el, s) => !(s && s.prevId) || null,
    'aria-label': (el, s) =>
      el
        .call('polyglot', 'workspace.personalize.moveUp')
        .replace('{app}', el.call('polyglot', (s && (s.title || s.id)) || '')),
    onClick: (e, el, s) => {
      e.stopPropagation()
      if (s && s.prevId) el.call('homeLookMoveApp', s.id, s.prevId)
    }
  },
  CompactIconButton_down: {
    icon: 'chevronDown',
    disabled: (el, s) => !(s && s.nextId) || null,
    'aria-label': (el, s) =>
      el
        .call('polyglot', 'workspace.personalize.moveDown')
        .replace('{app}', el.call('polyglot', (s && (s.title || s.id)) || '')),
    onClick: (e, el, s) => {
      e.stopPropagation()
      if (s && s.nextId) el.call('homeLookMoveApp', s.id, s.nextId)
    }
  }
}

// One sky of the Background group: its art in miniature (the files-plane mask
// the ambient ground paints, here in the caption ink) and its name; the chosen
// one takes the selection wash. A toggle button (aria-pressed), like the
// kit's option groups.
export const HomeSkyTile = {
  tag: 'button',
  type: 'button',
  class: 'home-sky-tile',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 'X',
  minWidth: '0',
  padding: 'X',
  border: 'none',
  round: 'radiusControl',
  background: 'transparent',
  color: 'caption',
  cursor: 'pointer',
  transition: 'background-color A defaultBezier, color A defaultBezier',
  ariaPressed: (el, s) => (el.call('homeSkyActive', s) === (s && s.value) ? 'true' : 'false'),
  '&[aria-pressed="true"]': { background: 'selected', color: 'title' },
  '&:not([aria-pressed="true"]):hover': { background: 'hover', color: 'title' },
  ':active': { background: 'selected' },
  onClick: (e, el, s) => {
    e.stopPropagation()
    if (s && s.value) el.call('homeLookSet', { sky: s.value })
  },

  SkyPreview: {
    position: 'relative',
    display: 'block',
    width: '100%',
    height: 'C',
    theme: 'layer',
    round: 'radiusControl',
    SkyArt: {
      position: 'absolute',
      inset: '0',
      background: 'caption',
      hide: (el, s) => !s || s.value === 'none',
      maskImage: (el, s) =>
        el.call(
          'ambientMaskImage',
          s && s.value !== 'none' ? el.call('ambientSkyUrl', s.value) : null,
          null,
          'none'
        ),
      maskSize: 'cover',
      maskPosition: 'center bottom',
      maskRepeat: 'no-repeat'
    }
  },
  SkyName: {
    tag: 'span',
    fontSize: 'Z',
    lineHeight: '1.4',
    textAlign: 'start',
    overflowWrap: 'normal',
    text: (el, s) => el.call('polyglot', (s && s.label) || '')
  }
}

export const HomePersonalize = {
  class: 'home-personalize',
  display: 'flex',
  flexDirection: 'column',
  gap: 'B',
  minWidth: '0',

  // ── App strip ──────────────────────────────────────────────────────────
  HomePersonalizeGroup_strip: {
    CaptionTitle: { Text: { text: '{{ workspace.personalize.appStrip | polyglot }}' } },

    HomePersonalizeSwitch_strip: {
      RowLabel: { text: '{{ workspace.personalize.showStrip | polyglot }}' },
      SwitchInput: {
        onCheck: (el, s) => (el.call('homeLookRead', s.root) || {}).stripShown,
        onClick: (e, el, s) => {
          e.preventDefault()
          el.call('homeLookSet', {
            stripShown: !(el.call('homeLookRead', s.root) || {}).stripShown
          })
        }
      }
    },

    HomePersonalizeChoice_style: {
      RowLabel: { text: '{{ workspace.personalize.style | polyglot }}' },
      AppSegmented: {
        compact: true,
        ariaLabel: (el) => el.call('polyglot', 'workspace.personalize.style'),
        current: (el, s) => (el.call('homeLookRead', s.root) || {}).stripStyle,
        select: 'homeLookSetStripStyle',
        childrenAs: 'state',
        children: () => [
          { key: 'dock', text: 'workspace.personalize.styleDock', value: 'dock' },
          { key: 'rail', text: 'workspace.personalize.styleRail', value: 'rail' }
        ]
      }
    },

    HomePersonalizeSwitch_labels: {
      RowLabel: { text: '{{ workspace.personalize.showLabels | polyglot }}' },
      SwitchInput: {
        onCheck: (el, s) => (el.call('homeLookRead', s.root) || {}).stripLabels,
        onClick: (e, el, s) => {
          e.preventDefault()
          el.call('homeLookSet', {
            stripLabels: !(el.call('homeLookRead', s.root) || {}).stripLabels
          })
        }
      }
    },

    // Nika 2026-10-02: "on navbar make icons optional from personalize default to off" — the navbar tabs'
    // glyph plates (and the phone strip's), off by default (functions/homeLook.js `navbarIcons`).
    HomePersonalizeSwitch_navbarIcons: {
      RowLabel: { text: '{{ workspace.personalize.navbarIcons | polyglot }}' },
      SwitchInput: {
        onCheck: (el, s) => !!(el.call('homeLookRead', s.root) || {}).navbarIcons,
        onClick: (e, el, s) => {
          e.preventDefault()
          el.call('homeLookSet', {
            navbarIcons: !(el.call('homeLookRead', s.root) || {}).navbarIcons
          })
        }
      }
    },

    AppsHint: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      lineHeight: '1.5',
      color: 'caption',
      overflowWrap: 'normal',
      text: '{{ workspace.personalize.appsHint | polyglot }}'
    },

    // The page supplies the rows (pages/main.js: the App strip's own rows,
    // each with its neighbours' ids).
    AppsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 'W',
      minWidth: '0',
      childExtends: 'HomePersonalizeApp',
      childrenAs: 'state',
      children: []
    }
  },

  // ── Theme ──────────────────────────────────────────────────────────────
  HomePersonalizeGroup_theme: {
    CaptionTitle: { Text: { text: '{{ workspace.personalize.theme | polyglot }}' } },
    AppSegmented: {
      compact: true,
      ariaLabel: (el) => el.call('polyglot', 'workspace.personalize.theme'),
      current: (el, s) => s.root.themeMode || 'auto',
      select: 'homeLookSetTheme',
      childrenAs: 'state',
      children: () => [
        { key: 'auto', text: 'workspace.personalize.themeAuto', value: 'auto' },
        { key: 'light', text: 'workspace.personalize.themeLight', value: 'light' },
        { key: 'dark', text: 'workspace.personalize.themeDark', value: 'dark' }
      ]
    }
  },

  // ── Background ─────────────────────────────────────────────────────────
  HomePersonalizeGroup_background: {
    CaptionTitle: { Text: { text: '{{ workspace.personalize.background | polyglot }}' } },
    SkyGrid: {
      role: 'group',
      'aria-label': (el) => el.call('polyglot', 'workspace.personalize.background'),
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 'Y',
      childExtends: 'HomeSkyTile',
      childrenAs: 'state',
      // Every sky the ambient files index holds, then None
      // (functions/homeLook.js homeSkyChoices).
      children: (el) => el.call('homeSkyChoices') || []
    },
    // The way back to the workspace's own sky — only while the person has one.
    AppCapsuleBtnGhost: {
      compact: true,
      alignSelf: 'flex-start',
      hide: (el, s) => (el.call('homeLookRead', s.root) || {}).sky == null,
      text: '{{ workspace.personalize.skyFollow | polyglot }}',
      onClick: (e, el) => {
        e.stopPropagation()
        el.call('homeLookSet', { sky: null })
      }
    }
  },

  // ── Sky layout ─────────────────────────────────────────────────────────
  // Where the sky sits (DESIGN-WL-HOME-SKY-TOP-GROUND-1, Nika 2026-10-01:
  // "keep bottom moving clouds as one of the options … clouds and moutain
  // should be the default"). `homeLook.skyLayout` — the same decision path
  // as every row here (functions/homeLook.js homeLookSetSkyLayout).
  // A GROUP of its own, captioned like Summary style and Widgets (live look on
  // dev e38425a28, 2026-10-02: as a row its title-case label sat between
  // those uppercase captions).
  HomePersonalizeGroup_skyLayout: {
    CaptionTitle: { Text: { text: '{{ workspace.personalize.skyLayout | polyglot }}' } },
    AppSegmented: {
      compact: true,
      ariaLabel: (el) => el.call('polyglot', 'workspace.personalize.skyLayout'),
      current: (el, s) => (el.call('homeLookRead', s.root) || {}).skyLayout || 'top',
      select: 'homeLookSetSkyLayout',
      childrenAs: 'state',
      children: () => [
        { key: 'top', text: 'workspace.personalize.skyLayoutTop', value: 'top' },
        { key: 'bottom', text: 'workspace.personalize.skyLayoutBottom', value: 'bottom' }
      ]
    }
  },

  // ── Summary ────────────────────────────────────────────────────────────
  HomePersonalizeGroup_summary: {
    CaptionTitle: { Text: { text: '{{ workspace.personalize.summary | polyglot }}' } },
    AppSegmented: {
      compact: true,
      ariaLabel: (el) => el.call('polyglot', 'workspace.personalize.summary'),
      current: (el, s) => (el.call('homeLookRead', s.root) || {}).summaryStyle,
      select: 'homeLookSetSummaryStyle',
      childrenAs: 'state',
      children: () => [
        { key: 'brief', text: 'workspace.personalize.summaryBrief', value: 'brief' },
        { key: 'signals', text: 'workspace.personalize.summarySignals', value: 'signals' }
      ]
    }
  },

  // ── Getting started ────────────────────────────────────────────────────
  // The new-account welcome on demand (Nika 2026-10-18: the assistant, the
  // tour, and a skip that is never a dead end): the setup conversation and
  // the quick tour, each one press away. Edit mode closes first — the drawer
  // would cover what the tour points at.
  HomePersonalizeGroup_start: {
    CaptionTitle: { Text: { text: '{{ workspace.onboarding.replayCaption | polyglot }}' } },
    StartActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 'Y',
      minWidth: '0',
      AppCapsuleBtnToolbar_wizard: {
        compact: true,
        Icon: { extends: 'Icon', name: 'displayPreferences', boxSize: 'iconSm' },
        Label: { tag: 'span', text: '{{ workspace.onboarding.replayWizard | polyglot }}' },
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('onboardingReplay', 'wizard')
        }
      },
      AppCapsuleBtnToolbar_assistant: {
        compact: true,
        Icon: { extends: 'Icon', name: 'magicstar', boxSize: 'iconSm' },
        Label: { tag: 'span', text: '{{ workspace.onboarding.replayAssistant | polyglot }}' },
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('onboardingReplay', 'assistant')
        }
      },
      AppCapsuleBtnToolbar_tour: {
        compact: true,
        Icon: { extends: 'Icon', name: 'playOval', boxSize: 'iconSm' },
        Label: { tag: 'span', text: '{{ workspace.onboarding.replayTour | polyglot }}' },
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('onboardingReplay', 'tour')
        }
      }
    }
  }
}
